import View from '../Support/View/View';
import Field from './Field';
import Btn from './Btn';
import Recaptcha from './Recaptcha';

export default class Form extends View {
    #fieldInstances   = new Map();
    #$submitButtonEl  = null;
    #submitButtonView = null;
    #recaptchaView    = null;
    #formSubmitMethod = null;
    #alertEl          = null;

    static get viewName() {
        return 'form';
    }
    static get viewType() {
        return 'component';
    }

    start() {
        this.#setFieldInstances();
        this.#setFieldEvents();

        this.addEventHandler('submit', this.#handleSubmission.bind(this));
    }

    /**
     * @returns {Map|Field[]}
     */
    get fields() {
        return this.#fieldInstances;
    }

    /**
     * @returns {Btn|null}
     */
    get submitButton() {
        if (this.#submitButtonView) {
            return this.#submitButtonView;
        }

        if (!this.#$submitButtonEl) {
            this.#$submitButtonEl = this.$el.querySelector('button[type="submit"]');

            if (!this.#$submitButtonEl) {
                const btns = this.$el.querySelectorAll('button');

                this.#$submitButtonEl = btns.length ? btns[btns.length - 1] : null;
            }
        }

        if (!this.#$submitButtonEl) {
            return null;
        }

        this.#submitButtonView = View.instances.get(this.#$submitButtonEl.dataset?.viewId)

        return this.#submitButtonView || null;
    }

    /**
     * @returns {Recaptcha|null}
     */
    get recaptcha() {
        if (this.#recaptchaView) {
            return this.#recaptchaView;
        }

        let recaptcha = this.$el.querySelector('[data-view-name="recaptcha"]');

        if (recaptcha) {
            recaptcha = View.instances.get(recaptcha.dataset.viewId);
        }

        if (recaptcha) {
            return this.#recaptchaView = recaptcha;
        }

        recaptcha = Array
            .from(document.querySelectorAll('[data-view-name="recaptcha"]'))
            .map(item => View.instances.get(item.dataset.viewId))
            .filter(instance => instance?.form?.viewId === this.viewId)[0];

        if (!recaptcha) {
            return null;
        }

        return this.#recaptchaView = recaptcha;
    }

    /**
     * @returns {boolean}
     */
    get isLoading() {
        return this.submitButton?.isLoading;
    }

    refreshFields() {
        // View.destroyInstances(this.$el.querySelectorAll('[data-view-name="field"]'));
        this.#fieldInstances.clear();

        // View.initInstances(this.$el.querySelectorAll('[data-view-name="field"]'));

        this.#setFieldInstances();
        this.#setFieldEvents();
    }

    /**
     * @param {string} name
     * @returns {Field|null}
     */
    field(name) {
        let field = this.#fieldInstances.get(name);

        if (field instanceof Map) {
            field = field.values().next().value;
        }

        return field || null;
    }

    /**
     * @param {string} name
     * @returns {HTMLElement|null}
     */
    error(name) {
        return this.field(name)?.error;
    }

    /**
     * @param {string} name
     * @returns {boolean}
     */
    hasError(name) {
        return !!this.error(name);
    }

    /**
     * @returns {boolean}
     */
    get hasErrors() {
        const loop = (items) => Array.from(items).filter(item => !(item instanceof Map) ? !!item.error : loop(item)).length > 0;

        return loop(this.fields.values());
    }

    /**
     * @param {boolean} name
     */
    setLoadingStateTo(value) {
        this.submitButton?.setLoadingStateTo(value);
    }

    /**
     * @param {boolean} shouldIgnoreErrors
     * @returns {boolean}
     */
    isValid(shouldIgnoreErrors = false) {
        if (shouldIgnoreErrors) {
            return this.$el.checkValidity();
        }

        return this.$el.checkValidity() && !this.hasErrors;
    }

    /**
     * @param {string} html
     */
    setAlert(html) {
        if (!(html || '').trim()) {
            return;
        }

        if (this.#alertEl) {
            this.#alertEl?.remove();
        }

        const container = document.createElement('div');

        container.innerHTML = html;

        this.#alertEl = container.firstElementChild;

        this.$el.insertBefore(this.#alertEl, this.$el.firstElementChild);

        if (typeof this.#alertEl.scrollIntoView === 'function') {
            // @see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
            this.#alertEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }
    }

    removeAlert() {
        this.#alertEl?.remove();

        this.#alertEl = null;
    }

    /**
     * @returns {Boolean}
     */
    get hasData() {
        return !!Array.from(this.data.values()).filter(value => !!value).length;
    }

    /**
     * @returns {Boolean}
     */
    get isEmpty() {
        return !this.hasData;
    }

    /**
     * @returns {FormData}
     */
    get data() {
        const data  = new FormData;

        const value = (field) => {
            if (['radio', 'checkbox'].includes(field.type)) {
                return field.isChecked ? (field.value || field.isChecked) : null;
            }

            if (field.type === 'file') {
                if (!field.files?.length) {
                    return null;
                }

                if (!field.allowsMultiple) {
                    return field.files[0];
                }

                return Array.from(field.files);
            }

            if ((field.type === 'select') && field.allowsMultiple) {
                return Array.from(field.$el.selectedOptions).map(option => option.value || option.text);
            }

            return field.value;
        };
        const acceptsArrayValues = (field) => (
            (
                ['select', 'file'].includes(field.type)
                && field.allowsMultiple
            )
            || (field.type === 'checkbox')
        );
        const append = (field) => {
            let val = value(field);

            if (!this.#isFilled(val)) {
                return;
            }

            if (!acceptsArrayValues(field)) {
                data.append(field.name, !Array.isArray(val) ? val : val[0]);

                return;
            }

            if (!Array.isArray(val)) {
                val = [val];
            }

            val.forEach(_val => data.append(field.name, _val));
        }

        this.fields.forEach(field => {
            if (!(field instanceof Map)) {
                append(field);

                return;
            }

            field.forEach(append);
        });

        Array.from(data.entries()).forEach(([name, value]) => {
            if (!this.#isFilled(value)) {
                data.delete(name);
            }
        });

        return data;
    }

    reset() {
        this.setLoadingStateTo(false);
        this.removeAlert();

        this.fields.forEach(field => {
            if (!(field instanceof Map)) {
                field.reset();

                return;
            }

            field.forEach(item => item.reset());
        });

        setTimeout(() => {
            this.submitButton?.setDisabledStateTo(!this.canSubmit);
        }, 0);
    }

    /**
     * @returns {Boolean}
     */
    get canSubmit() {
        return true;

        // if (this.isLegacyBrowser) {
        //     return true;
        // }

        // return this.isValid();
    }

    /**
     * @param {function} callback
     * @returns {Promise}
     */
    async submit(callback = null) {
        if (!this.canSubmit) {
            return Promise.reject(new Error('Invalid form values'));
        }

        let token = null;

        if (this.recaptcha) {
            try {
                token = await this.recaptcha.execute();
            } catch (error) {
                return Promise.reject(new Error(error));
            }
        }

        this.removeAlert();
        this.setLoadingStateTo(true);

        const request = callback ? callback(this, token) : this.#sendSubmitRequest(token);

        if (!request || (typeof request?.then !== 'function')) {
            this.setLoadingStateTo(false);

            return Promise.resolve();
        }

        return request
            .then(response => {
                this.reset();

                if (response?.data?.alert) {
                    this.setAlert(response.data.alert);
                }

                let responseURL = response?.request?.responseURL;

                // For legacy browsers
                if (!responseURL && !('responseURL' in XMLHttpRequest.prototype) && response.data) {
                    // This could be an another option and would require the server to set a custom header
                    // responseURL = response?.request?.getResponseHeader('X-Redirect-To');

                    const html = document.createElement('html');

                    html.innerHTML = response.data;

                    responseURL = html.querySelector('link[rel="canonical"]')?.href;
                }

                if (responseURL && (responseURL !== location.href) && (responseURL !== response?.config?.url)) {
                    location.href = responseURL;

                    return;
                }
            })
            .catch(({ response }) => {
                this.setLoadingStateTo(false);

                if (response?.status !== 422) {
                    if (response?.data?.alert) {
                        this.setAlert(response.data.alert);
                    }

                    return;
                }

                const errors = response?.data?.errors ?? {};
                const keys   = Object.keys(errors);

                keys.forEach(key => {
                    if (!key.includes('.')) {
                        this.field(key)?.setError(errors[key][0] ?? errors[key]);

                        return;
                    }

                    this.field(key.split('.')[0])?.setError(errors[key][0] ?? errors[key]);
                });
            });
    }

    #setFieldInstances() {
        Array.from(this.$el.elements)
            .filter(element => element?.dataset?.viewName === 'field')
            .map(field => View.instances.get(field.dataset.viewId))
            .filter(instance => !!instance)
            .forEach(instance => this.#setFieldInstance(instance));
    }
    #setFieldInstance(instance) {
        if (!this.#fieldInstances.has(instance.name)) {
            this.#fieldInstances.set(instance.name, instance);

            return;
        }

        let items = this.#fieldInstances.get(instance.name);

        if (!(items instanceof Map)) {
            const map = new Map();

            map.set(items, items);

            items = map;
        }

        items.set(instance, instance);

        this.#fieldInstances.set(instance.name, items);
    }

    #setFieldEvents() {
        const setFieldEvents = (field) => {
            field.addEventHandler('blur', () => {
                // @see https://developer.mozilla.org/en-US/docs/Web/API/Constraint_validation

                if (field.$el.validity.valid) {
                    return;
                }

                field.setError(field.$el.validationMessage);
            });

            if (!this.submitButton) {
                return;
            }

            field.addEventHandler('input', () => this.submitButton?.setDisabledStateTo(!this.canSubmit));
            field.addEventHandler('view:field.error', () => this.submitButton?.setDisabledStateTo(!this.canSubmit));
        };

        this.fields.forEach(field => {
            if (!(field instanceof Map)) {
                setFieldEvents(field);

                return;
            }

            field.forEach(item => setFieldEvents(item));
        });

        this.submitButton?.setDisabledStateTo(!this.canSubmit);
    }

    #isFilled(value) {
        if ([undefined, null, 'undefined', 'null'].includes(value)) {
            return false;
        }

        if (('' + value).trim() === '') {
            return false;
        }

        if (value === false || value === true) {
            return true;
        }

        if (value === 0 || value === '0') {
            return true;
        }

        return !!value;
    }

    /**
     * @param {SubmitEvent} e
     */
    #handleSubmission(e) {
        e.preventDefault();

        this.submit();
    }

    /**
     * @param {String|null} recaptchaToken
     */
    #sendSubmitRequest(recaptchaToken = null) {
        if (!this.#formSubmitMethod) {
            this.#formSubmitMethod = this.$el.querySelector('[name="_method"]')?.value ?? this.$el.method ?? 'POST';
        }

        const data = this.data;

        if (recaptchaToken) {
            data.append('captcha', recaptchaToken);
        }

        return axios({
            method: this.#formSubmitMethod,
            url: this.$el.action,
            data,
        });
    }
}